import React, { useEffect, useRef, useState } from 'react';
import HotelObserver from '../TwoD/Components/Views/HotelObserver';
import { useCompetitorTargeting } from './useCompetitorTargeting';

import './CompetitorsWindow.css';

const PADDING = 4;
const GAP = 6;
// Board aspect (height / width) used only until a real board is
// measured. HotelObserver is width-driven and proportional, so the
// measured aspect is width-invariant — one sample is enough.
const ASPECT_ESTIMATE = 1.6;

// pickGrid chooses the cols x rows arrangement that maximizes the
// rendered board size while keeping EVERY board on screen (no scroll).
// rows = ceil(count / cols) guarantees all boards have a slot. For
// each column count the resulting board width is
// min(cellWidth, cellHeight / aspect); we keep the layout with the
// largest result. Wide windows trend toward more columns, tall windows
// toward more rows, and large windows simply produce larger cells.
export function pickGrid(count, width, height, aspect) {
    if (count <= 0) return { cols: 1, rows: 1 };
    let best = { cols: 1, rows: count, size: -1 };
    for (let cols = 1; cols <= count; cols++) {
        const rows = Math.ceil(count / cols);
        const cellW = (width - (cols - 1) * GAP) / cols;
        const cellH = (height - (rows - 1) * GAP) / rows;
        if (cellW <= 0 || cellH <= 0) continue;
        const size = Math.min(cellW, cellH / aspect);
        if (size > best.size) {
            best = { cols: cols, rows: rows, size: size };
        }
    }
    return { cols: best.cols, rows: best.rows };
}

// CompetitorsWindow renders every seated player's HotelObserver board
// inside the Desktop preset's Competitors FloatingWindow. It measures
// the window, picks the best-fit grid, and applies a single uniform
// scale so all boards are always fully visible (never scrolled off)
// and as large as the window allows. The scale is a CSS transform —
// it does not change offsetWidth/offsetHeight, so HotelObserver's own
// measurement stays intact and there is no resize loop. Defaults to
// full-size (scale 1) and never hides, so a measurement hiccup shows
// the boards rather than a blank. HotelObserver is consumed as-is.
function CompetitorsWindow(props) {
    const gameData = props.gameData;
    const seatedPlayers = gameData
        ? gameData.gameplayer_set.filter(
            (gp) => gp.faction !== null && gp.faction !== undefined)
        : [];
    const playerCount = seatedPlayers.length;
    const rootRef = useRef(null);
    const boardRef = useRef(null);
    const [layout, setLayout] = useState({ cols: 3, rows: 2, scale: 1 });
    const { isTargetCandidate, onTargetClick } = useCompetitorTargeting();

    useEffect(() => {
        const root = rootRef.current;
        if (!root || playerCount === 0) return undefined;
        const recompute = () => {
            const W = root.clientWidth - PADDING * 2;
            const H = root.clientHeight - PADDING * 2;
            if (W <= 0 || H <= 0) return;
            // offsetWidth/offsetHeight ignore the CSS transform, so
            // this is the board's natural, scale-invariant aspect.
            const board = boardRef.current;
            let aspect = ASPECT_ESTIMATE;
            if (board && board.offsetWidth && board.offsetHeight) {
                aspect = board.offsetHeight / board.offsetWidth;
            }
            const grid = pickGrid(playerCount, W, H, aspect);
            const cellW = (W - (grid.cols - 1) * GAP) / grid.cols;
            const cellH = (H - (grid.rows - 1) * GAP) / grid.rows;
            const naturalH = cellW * aspect;
            const scale = naturalH > 0
                ? Math.min(1, cellH / naturalH)
                : 1;
            setLayout((prev) =>
                (prev.cols === grid.cols
                    && prev.rows === grid.rows
                    && Math.abs(prev.scale - scale) < 0.01)
                    ? prev
                    : { cols: grid.cols, rows: grid.rows, scale: scale });
        };
        recompute();
        const ro = new ResizeObserver(recompute);
        ro.observe(root);
        if (boardRef.current) ro.observe(boardRef.current);
        return () => {
            ro.disconnect();
        };
    }, [playerCount]);

    if (!gameData) return null;

    const rootStyle = {
        gridTemplateColumns:
            'repeat(' + layout.cols + ', minmax(0, 1fr))',
        gridTemplateRows:
            'repeat(' + layout.rows + ', minmax(0, 1fr))',
    };
    const boardStyle = {
        transform: 'scale(' + layout.scale + ')',
    };

    return (
        <div className={'competitors-window'}
             ref={rootRef}
             style={rootStyle}>
            {seatedPlayers.map((gp, key) => (
                <div className={'competitors-window__cell'} key={key}>
                    <div className={'competitors-window__board'}
                         ref={key === 0 ? boardRef : null}
                         style={boardStyle}>
                        <HotelObserver
                            gamePlayerData={gp}
                            isActive={gameData.active_player === gp.id}
                            isTargetCandidate={isTargetCandidate(gp.id)}
                            onTargetClick={() => onTargetClick(gp.id)}/>
                    </div>
                </div>
            ))}
        </div>
    );
}

export default CompetitorsWindow;
